import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";

import { cn } from "@/lib/utils";
import { useSetAtom } from "jotai";
import { setOverlayOpenAtom } from "@/lib/overlayAtoms";

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  const id = React.useId();
  const overlayId = React.useMemo(() => `dialog:${id}` as const, [id]);
  const setOverlayOpen = useSetAtom(setOverlayOpenAtom);

  const isControlled = props.open !== undefined;
  const initialOpen = props.open ?? props.defaultOpen ?? false;

  // Seed initial state and cleanup on unmount
  React.useEffect(() => {
    setOverlayOpen({ id: overlayId, open: Boolean(initialOpen) });
    return () => setOverlayOpen({ id: overlayId, open: false });
    // We intentionally run only once on mount/unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // In controlled mode, keep store in sync with `open`
  React.useEffect(() => {
    if (isControlled) {
      setOverlayOpen({ id: overlayId, open: Boolean(props.open) });
    }
  }, [isControlled, props.open, overlayId, setOverlayOpen]);

  const userOnOpenChange = props.onOpenChange;
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      userOnOpenChange?.(next);
      if (!isControlled) {
        setOverlayOpen({ id: overlayId, open: next });
      }
    },
    [userOnOpenChange, isControlled, setOverlayOpen, overlayId],
  );

  return (
    <DialogPrimitive.Root
      data-slot="dialog"
      {...props}
      onOpenChange={handleOpenChange}
    />
  );
}

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80 backdrop-blur-[1.5px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    overlayClassName?: string;
  }
>(({ className, children, style, overlayClassName, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay className={overlayClassName} />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        className,
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed top-[50%] left-[50%] z-50 grid w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-4 duration-300 sm:max-w-lg bg-transparent border-0 outline-none ring-0 shadow-none focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0",
      )}
      style={{
        boxShadow: "none !important",
        outline: "none !important",
        border: "none !important",
        ...style,
      }}
      {...props}
    >
      {children}
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-1.5 text-center sm:text-left",
      className,
    )}
    {...props}
  />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2",
      className,
    )}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
